import mixpanel from "mixpanel-browser";

import { formatFollowersCount } from "@/Helpers/format-helper";
import { getRandomProfileEmoji, OpenInNewWindow } from "@/Helpers/helper";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcnComponents/ui/accordion";
import { FaLinkedinIn } from "react-icons/fa";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcnComponents/ui/alert-dialog";

export const DraftRowAccordion = ({
  Header,
  ContentArray,
  defaultValue,
}: {
  Header: React.ReactNode;
  ContentArray: React.ReactNode[];
  defaultValue?: string;
}) => {
  return (
    <>
      <Accordion
        type="single"
        defaultValue={defaultValue}
        collapsible
        className="w-full border-t-2 border-b-2 border-t-[#EEE] border-b-[#EEE]"
      >
        <AccordionItem className="w-full" value={defaultValue || "my-value"}>
          <AccordionTrigger className="w-full px-3 py-4">
            {Header}
          </AccordionTrigger>
          <AccordionContent className="w-full">
            <div className="flex w-full flex-col items-start gap-[22px] shrink-0 self-stretch">
              {ContentArray.map((content, index) => (
                <div className="w-full" key={index}>
                  {content}
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export const DraftRowHeader = ({ columnArray, columnCount }) => {
  const len = columnCount || columnArray?.length;
  return (
    <div className={cn("w-full grid mr-[60px]", `grid-cols-${len}`)}>
      {columnArray?.map((column, index) => (
        <div
          key={index}
          className="flex flex-wrap items-center justify-start p-1 overflow-hidden text-[color:var(--Natural-800,#424242)] text-ellipsis text-sm not-italic font-normal leading-5"
        >
          {column}
        </div>
      ))}
    </div>
  );
};

export const DraftColInfluDetails = ({
  Influencer,
  disableProfileLink = true,
}) => {
  return (
    <div className="flex gap-3 items-center justify-center ">
      <img
        className="w-[40px] h-[40px] rounded-md shrink-0 "
        src={Influencer?.influencerDetails?.profile || getRandomProfileEmoji()}
        alt=""
      />
      <div className="flex flex-col gap-1 ">
        <span
          onClick={() => {
            if (disableProfileLink) return;
            mixpanel.track(
              "Brand Live/Review Campaign Details: Influencer Draft section : Influencer anchors Profile - clicked"
            );
            OpenInNewWindow({
              link: `${window.location.origin}/${Influencer?.influencerDetails?.slug}`,
            });
          }}
          className="text-[#000] font-[Inter] text-[14px] not-italic font-semibold leading-[20px] cursor-pointer"
        >
          {Influencer?.influencerDetails?.name}{" "}
        </span>
        <div className="flex gap-1">
          {" "}
          <FaLinkedinIn
            className="cursor-pointer"
            onClick={() => {
              mixpanel.track(
                "Brand Live/Review Campaign Details: Influencer Draft section : Influencer Linkedin - clicked"
              );
              OpenInNewWindow({
                link: Influencer?.influencerDetails?.linkedinLink,
              });
            }}
            size={18}
            color="#2770DD"
          />{" "}
          <span className="text-[#121212] text-sm not-italic font-normal leading-5">
            {formatFollowersCount(
              Influencer?.influencerDetails?.linkedinFollowers
            )}
          </span>{" "}
        </div>
      </div>
    </div>
  );
};

export const DraftColStatus = ({ Influencer }) => {
  return (
    <p className="text-[color:var(--Natural-800,#424242)] text-sm not-italic font-semibold leading-5">
      {Influencer?.brandApproval === "accepted" ? (
        <StatusBadge variant="APPROVED" value="Approved" />
      ) : Influencer?.draft?.[0]?.suggestedChanges?.comment?.length > 0 ? (
        <StatusBadge variant="SUGGESTED" value="Suggestion Recommended" />
      ) : Influencer?.draft?.length > 0 ? (
        Influencer?.draft?.length > 1 ? (
          <StatusBadge variant="DRAFT_SUBMITTED" value="Revised Draft" />
        ) : (
          <StatusBadge variant="DRAFT_SUBMITTED" value="Draft Submitted" />
        )
      ) : (
        <StatusBadge variant="DRAFT_PENDING" value="Draft Pending" />
      )}
    </p>
  );
};

interface StatusBadgeProps {
  variant: "APPROVED" | "SUGGESTED" | "DRAFT_SUBMITTED" | "DRAFT_PENDING";
  value: string;
}

const StatusBadge = ({ variant, value }: StatusBadgeProps) => {
  return (
    <span
      className={cn(
        "text-[12px] uppercase rounded-full px-4 py-2",
        {
          APPROVED: "text-teal-800 bg-[#8b8be333]",
          SUGGESTED: "text-[#5555FF] bg-[#A3A3FF33]",
          DRAFT_SUBMITTED: "text-[#86198F] bg-[#EE1EFF33]",
          DRAFT_PENDING: "text-[#854D0E] bg-[#F28C1933]",
        }[variant]
      )}
    >
      {value}
    </span>
  );
};

export const SuggestionAddedDialog = ({ onClose }) => {
  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Suggestion added and new draft received, please check
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Check</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const DraftApprovedDialog = ({ onClose, text }) => {
  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Draft is approved!</AlertDialogTitle>
          <AlertDialogDescription>
            Now you can set the live date
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {text ?? "Set Live Date"}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
